/**
 * autosaveV2.js — Standalone autosave adapter for Business Single Application V2.
 *
 * Modeled after buildBusinessEddAutoSavePayload.js but independent —
 * no import from BusinessPeriodicReview/helper.js.
 */

import _ from "lodash";
import { prepareBaseAutoSaveData, resolveAutoSaveSanitizeOptions } from "../autosave/shared";
import { createJsonConfiguredAutoSaveAdapter } from "../autosave/jsonAutoSave";
import {
    hydrateBusinessV2FieldValueMapFromApi,
    hydrateBusinessV2PayloadFromApi,
    normalizeBusinessV2FieldValueMapForApi,
    normalizeBusinessV2PayloadForApi,
} from "./compatibilityV2";
import { movePrimaryUserToFront } from "./helperV2";

const resolveBusinessV2AutoSaveConfig = (autoSaveConfig = null) => {
    const nextConfig = _.cloneDeep(autoSaveConfig || {});
    const primaryRecordKey = nextConfig?.primaryRecord || "primary";

    nextConfig.records = {
        ...(nextConfig?.records || {}),
        [primaryRecordKey]: {
            ...(nextConfig?.records?.[primaryRecordKey] || {}),
            saveMode: nextConfig?.records?.[primaryRecordKey]?.saveMode || "update-field",
            saveEndpoint: nextConfig?.records?.[primaryRecordKey]?.saveEndpoint || "/api/business/update-field",
        },
    };

    return nextConfig;
};

/**
 * Normalize business data before autosave, applying V2-specific rules.
 */
const prepareBusinessV2Data = (data, autoSaveConfig) => {
    const prepared = prepareBaseAutoSaveData(data, resolveAutoSaveSanitizeOptions(autoSaveConfig));

    if (!prepared || typeof prepared !== "object") {
        return prepared;
    }

    // ── Individual normalization ──
    prepared.individuals = movePrimaryUserToFront(prepared.individuals || []);

    if (Array.isArray(prepared.individuals) && prepared.individuals.length > 0) {
        prepared.individuals = prepared.individuals.map((individual, index) => {
            if (!_.isPlainObject(individual)) {
                return individual;
            }

            const next = _.cloneDeep(individual);

            // Primary user enforcement (index 0)
            if (index === 0) {
                next.isPrimaryUser = true;
                if (!next.email && prepared.email) {
                    next.email = prepared.email;
                }
                if (!next.accessType) {
                    next.accessType = "NoGroup";
                }
                next.isAuthorizedUser = true;
            }

            return next;
        });
    }

    // ── Entity cleanup ──
    if (!prepared.hasEntity) {
        prepared.entities = [];
    }

    // ── Phone country code cleanup ──
    if (!prepared.landlinePhone) {
        delete prepared.landlineCountryCode;
    }
    if (!prepared.mobilePhone) {
        delete prepared.mobileCountryCode;
    }

    return prepared;
};

/**
 * Create the V2-specific autosave adapter.
 * Uses the shared JSON-configured autosave infrastructure.
 */
export const createBusinessV2AutoSaveAdapter = (fieldDefinitions = {}, autoSaveConfig = null) =>
{
    const resolvedAutoSaveConfig = resolveBusinessV2AutoSaveConfig(autoSaveConfig);
    const baseAdapter = createJsonConfiguredAutoSaveAdapter({
        autoSaveConfig: resolvedAutoSaveConfig,
        fieldDefinitions: _.cloneDeep(fieldDefinitions || {}),
        prepareData(data) {
            return prepareBusinessV2Data(data, resolvedAutoSaveConfig);
        },
    });

    return {
        ...baseAdapter,
        buildOperations({ snapshot, current }) {
            const plan = baseAdapter.buildOperations({ snapshot, current });
            const operations = Array.isArray(plan?.operations) ? plan.operations : [];

            return {
                ...plan,
                operations: operations.map((operation) => {
                    if (!operation?.payload || typeof operation.payload !== "object") {
                        return operation;
                    }

                    if (operation?.payload?.fieldValueMap && typeof operation.payload.fieldValueMap === "object") {
                        const shouldSanitizeHiddenPrimaryFields = String(operation?.endpoint || "").includes("/api/business/update-field");
                        return {
                            ...operation,
                            payload: {
                                ...operation.payload,
                                fieldValueMap: normalizeBusinessV2FieldValueMapForApi(
                                    operation.payload.fieldValueMap,
                                    fieldDefinitions,
                                    shouldSanitizeHiddenPrimaryFields ? current : null,
                                    shouldSanitizeHiddenPrimaryFields ? snapshot : null
                                ),
                            },
                        };
                    }

                    if (operation?.type === "full") {
                        return {
                            ...operation,
                            payload: normalizeBusinessV2PayloadForApi(operation.payload, fieldDefinitions),
                        };
                    }

                    return operation;
                }),
            };
        },
        applyOperationResult(data, operation, responseData) {
            const hydratedOperation = operation?.payload?.fieldValueMap
                ? {
                    ...operation,
                    payload: {
                        ...operation.payload,
                        fieldValueMap: hydrateBusinessV2FieldValueMapFromApi(operation.payload.fieldValueMap),
                    },
                }
                : (operation?.type === "full"
                    ? {
                        ...operation,
                        payload: hydrateBusinessV2PayloadFromApi(operation.payload),
                    }
                    : operation);

            const hydratedCurrentData = hydrateBusinessV2PayloadFromApi(data);
            const hydratedResponseData = hydrateBusinessV2PayloadFromApi(responseData);
            return hydrateBusinessV2PayloadFromApi(
                baseAdapter.applyOperationResult(hydratedCurrentData, hydratedOperation, hydratedResponseData)
            );
        },
    };
};
